import React, { useEffect, useState } from "react";
import { CreditCard, Download, ShieldCheck } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import Skeleton from "@/components/jea/Skeleton";
import { demoClient, useDemo } from "@/pages/demo/DemoShell";
import { formatMoney } from "@/lib/pricing";
import { cn } from "@/lib/utils";

const shortDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};

const DemoBillingPage = () => {
  const demo = useDemo();
  const [data, setData] = useState({ subscription: {}, events: [], ytd_spend_cents: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await demoClient.get("/demo/billing/summary");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const nudge = (feature) => () => demo?.openSignUp(feature);
  const sub = data.subscription || {};
  const pm = sub.payment_method || {};

  return (
    <div className="space-y-6" data-testid="demo-billing-page">
      <div>
        <div className="jea-caption">Subscription & Charges</div>
        <h1 className="text-[28px] font-bold text-white">Billing</h1>
        <p className="text-text-secondary text-[14px] mt-1">
          One line-item receipt per query. Full ledger, downloadable receipts, and painless plan changes.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
        <JeaCard>
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={14} className="text-accent-pink" />
            <div className="jea-caption">Current Plan</div>
          </div>
          {loading ? <Skeleton className="h-24" /> : (
            <>
              <div className="text-white text-[24px] font-extrabold tracking-tight">
                {sub.plan_type?.toUpperCase() || "PRO"} · {(sub.path || "annual").toUpperCase()}
              </div>
              <div className="text-text-secondary text-[13px] mt-1">
                {(sub.tier || "silver").toUpperCase()} tier · {formatMoney(sub.price_cents || 0)}/year
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4 text-[13px]">
                <Metric label="Status" value={<Pill value={sub.status || "active"} />} />
                <Metric label="Started" value={shortDate(sub.started_at)} />
                <Metric label="Next Renewal" value={shortDate(sub.renews_at)} />
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <JeaButton variant="outline-pink" size="sm" onClick={nudge("changing plans")} data-testid="demo-billing-change-plan">
                  Change Plan
                </JeaButton>
                <JeaButton variant="outline-neutral" size="sm" onClick={nudge("canceling subscriptions")} data-testid="demo-billing-cancel">
                  Cancel Subscription
                </JeaButton>
              </div>
            </>
          )}
        </JeaCard>

        <JeaCard>
          <div className="jea-caption">YTD Spend</div>
          <div className="jea-mono text-accent-pink font-extrabold text-[32px] mt-2" data-testid="demo-billing-ytd">
            {formatMoney(data.ytd_spend_cents || 0)}
          </div>
          <div className="text-text-secondary text-[12px] mt-2">
            {data.events.length} billing event{data.events.length === 1 ? "" : "s"} recorded
          </div>
        </JeaCard>
      </div>

      <JeaCard>
        <div className="flex items-center gap-2 mb-3">
          <CreditCard size={14} className="text-accent-pink" />
          <div className="jea-caption">Payment Method</div>
        </div>
        {loading ? <Skeleton className="h-16" /> : (
          <div className="flex items-center justify-between flex-wrap gap-3 bg-bg-tertiary rounded-[8px] p-4">
            <div>
              <div className="text-white text-[14px] font-semibold">
                {pm.brand || "Visa"} ending in •••• {pm.last4 || "4242"}
              </div>
              {pm.exp_month && pm.exp_year && (
                <div className="text-text-secondary text-[12px] mt-1">
                  Expires {String(pm.exp_month).padStart(2, "0")}/{pm.exp_year}
                </div>
              )}
            </div>
            <JeaButton variant="outline-pink" size="sm" onClick={nudge("updating payment methods")} data-testid="demo-billing-update-pm">
              Update Payment Method
            </JeaButton>
          </div>
        )}
      </JeaCard>

      <JeaCard className="p-0 overflow-hidden">
        <div className="p-6 pb-3">
          <h2 className="text-white font-bold text-[16px]">Billing History</h2>
        </div>
        {loading ? <Skeleton className="h-40" /> : data.events.length === 0 ? (
          <div className="p-8 text-text-secondary text-[13px] text-center">No billing events yet.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-t border-border-default">
                {["Date", "Description", "Amount", "Status", "Receipt"].map((h) => (
                  <th key={h} className="jea-caption text-text-secondary p-4 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.events.map((e) => (
                <tr key={e.id} className="border-t border-border-default hover:bg-bg-tertiary transition-colors" data-testid={`demo-billing-row-${e.id}`}>
                  <td className="p-4 jea-date">{shortDate(e.created_at)}</td>
                  <td className="p-4">
                    <div className="text-white text-[13px] font-semibold">{e.description}</div>
                    {e.debtor_name && (
                      <div className="text-text-secondary text-[11px]">{e.debtor_name}</div>
                    )}
                  </td>
                  <td className="p-4 jea-mono text-accent-pink font-bold">{formatMoney(e.amount_cents || 0)}</td>
                  <td className="p-4"><Pill value={e.status || "paid"} /></td>
                  <td className="p-4">
                    <button
                      type="button"
                      onClick={nudge("downloading receipts")}
                      className="text-accent-pink hover:text-accent-pink-light inline-flex items-center gap-1 text-[12px] font-semibold"
                      data-testid={`demo-billing-receipt-${e.id}`}
                    >
                      <Download size={12} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </JeaCard>
    </div>
  );
};

const Metric = ({ label, value }) => (
  <div>
    <div className="jea-caption">{label}</div>
    <div className="text-white font-semibold mt-1">{value}</div>
  </div>
);

const Pill = ({ value }) => {
  const v = (value || "").toString().toLowerCase();
  const style = v === "paid" || v === "active" ? "bg-success/15 text-success"
    : v === "canceling" || v === "pending" ? "bg-warning/15 text-warning"
    : v === "past_due" || v === "failed" ? "bg-danger/15 text-danger"
    : "bg-bg-tertiary text-text-secondary";
  return (
    <span className={cn("inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.08em] uppercase", style)}>
      {value}
    </span>
  );
};

export default DemoBillingPage;
